/**
 * Android-only UI parity: status-bar header inset, responsive scale, button seam fixes.
 * iOS callers receive identity values — safe to import everywhere.
 */
import { Dimensions, Platform, type TextStyle, type ViewStyle } from 'react-native';
import { androidStripElevationSeams } from './blurParity';

const BASE_WIDTH = 390;
const BASE_HEIGHT = 844;

export function isAndroidUi(): boolean {
  return Platform.OS === 'android';
}

/** Full status-bar inset for sticky / collapsing headers (never capped). */
export function androidHeaderTopInset(full_inset: number, extra = 0): number {
  if (!isAndroidUi()) {
    return full_inset;
  }
  return Math.max(full_inset, 0) + extra;
}

let cached_ui_scale: number | null = null;

/** Scale factor ≤ 1 on Android — refines oversized typography/layout on large devices. */
export function androidUiScaleFactor(): number {
  if (!isAndroidUi()) {
    return 1;
  }
  if (cached_ui_scale != null) {
    return cached_ui_scale;
  }
  const { width, height, fontScale } = Dimensions.get('window');
  const width_ratio = BASE_WIDTH / Math.max(width, 320);
  const height_ratio = BASE_HEIGHT / Math.max(height, 640);
  const density_scale = Math.min(width_ratio, height_ratio);
  const normalized = density_scale / Math.max(fontScale, 1);
  cached_ui_scale = Math.min(1, Math.max(0.84, normalized));
  return cached_ui_scale;
}

export function androidScaleFont(size: number): number {
  if (!isAndroidUi()) {
    return size;
  }
  return Math.round(size * androidUiScaleFactor() * 10) / 10;
}

export function androidScaleSize(size: number): number {
  if (!isAndroidUi()) {
    return size;
  }
  return Math.round(size * androidUiScaleFactor());
}

export function androidScaleSpace(size: number): number {
  return androidScaleSize(size);
}

export function androidDashboardHeaderExpandedHeight(): number {
  return isAndroidUi() ? androidScaleSize(68) : 68;
}

export function androidDashboardHeaderCollapsedHeight(): number {
  return isAndroidUi() ? androidScaleSize(48) : 48;
}

export function androidDashboardHeaderScrollThreshold(): number {
  return isAndroidUi() ? androidScaleSize(80) : 80;
}

export function androidAppHeaderContentHeight(compact = false): number {
  const base = compact ? 44 : 56;
  return isAndroidUi() ? androidScaleSize(base) : base;
}

/** Scroll padding below v3 glass sticky headers (Dashboard, Calendar, Account, …). */
export function androidGlassHeaderContentTop(full_inset: number, gap = 8): number {
  return androidHeaderTopInset(full_inset) + androidDashboardHeaderExpandedHeight() + androidScaleSpace(gap);
}

/** Scroll / body offset below a standard stack `ScreenHeader` (fixed bar). */
export function androidAppHeaderContentTop(full_inset: number, gap = 0): number {
  return androidHeaderTopInset(full_inset) + androidAppHeaderContentHeight() + androidScaleSpace(gap);
}

/** Merge iOS shadow tokens; flat surface on Android (no elevation seam). */
export function androidResolveShadow(ios_shadow: ViewStyle): ViewStyle {
  if (!isAndroidUi()) {
    return ios_shadow;
  }
  return androidStripElevationSeams();
}

/** Remove Android elevation shadow seams from clickable / pill surfaces. */
export function androidFlatSurfaceStyle(extra: ViewStyle = {}): ViewStyle {
  if (!isAndroidUi()) {
    return extra;
  }
  return { ...extra, ...androidStripElevationSeams() };
}

/** iOS shadow passthrough; flat surface on Android. */
export function androidPlatformShadow(ios_shadow: ViewStyle): ViewStyle {
  return androidResolveShadow(ios_shadow);
}

/** Scale numeric fields in a text style on Android only. */
export function androidScaleTextStyle(style: TextStyle): TextStyle {
  if (!isAndroidUi()) {
    return style;
  }
  const next: TextStyle = { ...style };
  if (typeof next.fontSize === 'number') {
    next.fontSize = androidScaleFont(next.fontSize);
  }
  if (typeof next.lineHeight === 'number') {
    next.lineHeight = androidScaleFont(next.lineHeight);
  }
  if (typeof next.letterSpacing === 'number') {
    next.letterSpacing = androidScaleFont(next.letterSpacing);
  }
  return next;
}
